import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Save, Menu } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const MENU_ITEMS = [
  { key: 'kurumsal', label: 'Kurumsal', path: '/kurumsal' },
  { key: 'projeler', label: 'Projeler', path: '/projeler' },
  { key: 'hizmetler', label: 'Hizmetlerimiz', path: '/hizmetlerimiz' },
  { key: 'ekatalog', label: 'E-Katalog', path: '/e-katalog' },
  { key: 'blog', label: 'Haberler', path: '/blog' },
  { key: 'galeri', label: 'Galeri', path: '/galeri' },
  { key: 'ekip', label: 'Ekip', path: '/ekip' },
  { key: 'sss', label: 'SSS', path: '/sss' },
  { key: 'referanslar', label: 'Referanslar', path: '/referanslar' },
  { key: 'iletisim', label: 'İletişim', path: '/iletisim' },
];

export default function MenuSettings() {
  const [menuConfig, setMenuConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api.get('/settings/menu')
      .then(r => {
        const data = { ...r.data };
        delete data.key;
        delete data._id;
        setMenuConfig(data);
      })
      .catch(() => {
        // Default: all visible
        const defaults = {};
        MENU_ITEMS.forEach(m => { defaults[m.key] = true; });
        setMenuConfig(defaults);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (key) => {
    setMenuConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.put('/settings/menu', menuConfig);
      toast.success('Menü ayarları güncellendi');
    } catch (err) {
      toast.error('Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-menu-settings">
      <h1 className="font-heading text-2xl font-semibold mb-2">Menü Ayarları</h1>
      <p className="text-sm text-muted-foreground mb-6">Sitede görünecek menü öğelerini seçin. Anasayfa her zaman görünür.</p>

      <form onSubmit={handleSubmit}>
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          {/* Always visible */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center">
              <Eye className="w-5 h-5 text-[hsl(var(--gold))]" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">Anasayfa</div>
              <div className="text-xs text-muted-foreground">/</div>
            </div>
            <span className="px-2.5 py-1 text-xs rounded-full bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))]">Her zaman görünür</span>
          </div>

          {/* Toggleable items */}
          <div className="space-y-2">
            {MENU_ITEMS.map(item => {
              const isVisible = menuConfig[item.key] !== false;
              return (
                <div
                  key={item.key}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${
                    isVisible ? 'border-border bg-card' : 'border-border/50 bg-secondary/30 opacity-60'
                  }`}
                  onClick={() => toggleItem(item.key)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isVisible ? 'bg-[hsl(var(--gold)/0.1)]' : 'bg-secondary'
                  }`}>
                    {isVisible ? <Eye className="w-5 h-5 text-[hsl(var(--gold))]" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.path}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleItem(item.key); }}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      isVisible ? 'bg-[hsl(var(--gold))]' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      isVisible ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
          data-testid="admin-save-button"
        >
          <Save className="w-4 h-4" />
          {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </form>
    </div>
  );
}
